import React, { FC, useRef } from 'react';
import { ListingCard } from '../cards/listing-card/listing-card';
import { ServiceCard } from '../cards/service-card/service-card';
import { Catalog } from '../../apis/search-catalog';
import striptags from 'striptags';
import styles from './styles.module.css';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useServiceRedirect } from '../../hooks/use-service-redirect';
import classNames from 'classnames';

type CardVariant = 'default' | 'service';
type CarouselLayout = 'full' | 'contained';

interface ServiceCarouselProps {
  items: Catalog[];
  onClickCatalog: (slug: string, categorySlug: string) => void;
  card?: CardVariant;
  onRequestService?: (catalog: Catalog) => void;
  layout?: CarouselLayout;
}

const STEP = 400;

export const ServiceCarousel: FC<ServiceCarouselProps> = ({
  items,
  onClickCatalog,
  card = 'default',
  onRequestService,
  layout = 'full',
}) => {
  const { redirectToService } = useServiceRedirect();
  
  const handleRequestService = onRequestService || ((catalog: Catalog) => redirectToService(catalog.link));
  const trackRef = useRef<HTMLDivElement>(null);

  const atStart = () => {
    const t = trackRef.current;
    return !!t && t.scrollLeft <= 0;
  };

  const atEnd = () => {
    const t = trackRef.current;
    return !!t && t.scrollLeft + t.clientWidth >= t.scrollWidth - 1;
  };

  const scrollLeft = () => {
    const t = trackRef.current;
    if (!t) return;

    if (atStart()) {
      t.scrollTo({ left: t.scrollWidth, behavior: 'smooth' });
    } else {
      t.scrollBy({ left: -STEP, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    const t = trackRef.current;
    if (!t) return;

    if (atEnd()) {
      t.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      t.scrollBy({ left: STEP, behavior: 'smooth' });
    }
  };

  const renderCard = (catalog: Catalog) => {
    if (card === 'service') {
      return (
        <ServiceCard
          key={catalog.slug}
          catalog={catalog}
          onClick={() => onClickCatalog(catalog.slug, catalog.categorySlug)}
          onSolicitar={() => handleRequestService(catalog)}
        />
      );
    }

    return (
      <ListingCard
        key={catalog.slug}
        title={catalog.title}
        category={catalog.category}
        subtitle={striptags(catalog.description)}
        type={catalog.isOnline ? 'online' : 'in-person'}
        onClick={() => onClickCatalog(catalog.slug, catalog.categorySlug)}
        onClickFavourite={() => {}}
        onClickOnlineService={() => handleRequestService(catalog)}
        disposition="card"
        isDigital={catalog.isDigital}
        department={catalog.department}
        href={`/carta-de-servicos/${catalog.slug}`}
      />
    );
  };

  const wrapperClassName = classNames(styles.carouselWrapper, {
    [styles.carouselWrapperContained]: layout === 'contained',
  });

  const trackClassName = classNames(styles.carouselTrack, {
    [styles.carouselTrackContained]: layout === 'contained',
  });

  const leftGradientClassName = classNames(styles.carouselGradientLeft, {
    [styles.carouselGradientLeftContained]: layout === 'contained',
  });

  const rightGradientClassName = classNames(styles.carouselGradientRight, {
    [styles.carouselGradientRightContained]: layout === 'contained',
  });

  return (
    <div className={wrapperClassName}>
      <div className={leftGradientClassName}>
        <button
          className={styles.carouselButtonLeft}
          aria-label="Anterior"
          onClick={scrollLeft}
        >
          <ChevronLeft />
        </button>
      </div>

      <div className={trackClassName} ref={trackRef}>
        {items?.map(renderCard)}
      </div>

      <div className={rightGradientClassName}>
        <button
          className={styles.carouselButtonRight}
          aria-label="Próximo"
          onClick={scrollRight}
        >
          <ChevronRight />
        </button>
      </div>
    </div>
  );
};
